import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { fetchUsers } from '../../services/fetchAllUser'
import { FaCity } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

interface CityCount {
  city: string
  count: number
}

const PopularCities: React.FC = () => {
  const { t } = useTranslation()
  const [popularCities, setPopularCities] = useState<CityCount[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const users = await fetchUsers()
        const cityCount = new Map<string, number>()

        users.forEach(user => {
          const city = user.address?.city
          if (city) {
            cityCount.set(city, (cityCount.get(city) || 0) + 1)
          }
        })

        const sortedCities = Array.from(cityCount.entries())
          .map(([city, count]) => ({ city, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 14)

        setPopularCities(sortedCities)
      } catch (error) {
        console.error('Failed to fetch cities:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCities()
  }, [])

  const handleCityClick = (city: string) => {
    const storedFilters = JSON.parse(
      localStorage.getItem('searchFilters') || '{}',
    )

    const updatedFilters = {
      ...storedFilters,
      iLiveIn: [city],
    }

    localStorage.setItem('searchFilters', JSON.stringify(updatedFilters))
    window.scrollTo(0, 0)
    window.location.href = '/'
  }

  if (isLoading) {
    return (
      <div className='lg:w-4/12 w-full pt-5'>
        <h3 className='text-base flex font-semibold mb-4 pb-2 border-b-2 border-redpink text-white justify-center xl:justify-start'>
          <FaCity className='mr-3 text-xl text-redpink' />
          {t('popular-cities')}
        </h3>
        <div className='grid grid-cols-2 gap-2'>
          {[...Array(14)].map((_, index) => (
            <div
              key={index}
              className='h-6 bg-anthraciteLight rounded animate-pulse'
            />
          ))}
        </div>
      </div>
    )
  }

  const midPoint = Math.ceil(popularCities.length / 2)
  const firstColumn = popularCities.slice(0, midPoint)
  const secondColumn = popularCities.slice(midPoint)

  return (
    <div className='lg:w-4/12 pt-5'>
      <h3 className='text-base flex font-semibold mb-4 pb-2 border-b-2 border-redpink text-white justify-center xl:justify-start'>
        <FaCity className='mr-3 text-xl text-redpink' />
        {t('popular-cities')}
      </h3>
      <div className='grid grid-cols-2 gap-4'>
        <div className='space-y-0'>
          <ul className='space-y-0'>
            {firstColumn.map(cityData => (
              <li key={cityData.city}>
                <Link
                  to='/'
                  onClick={() => {
                    handleCityClick(cityData.city)
                    window.scrollTo(0, 0)
                  }}
                  className='text-gray-300 block rounded py-1 hover:text-redpink hover:pl-2 hover:bg-anthraciteLight transition-all'
                >
                  {cityData.city}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className='space-y-0'>
          <ul className='space-y-0'>
            {secondColumn.map(cityData => (
              <li key={cityData.city}>
                <Link
                  to='/'
                  onClick={() => {
                    handleCityClick(cityData.city)
                    window.scrollTo(0, 0)
                  }}
                  className='text-gray-300 block rounded py-1 hover:text-redpink hover:pl-2 hover:bg-anthraciteLight transition-all'
                >
                  {cityData.city}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default PopularCities
